import {
  LoginRequest,
  LogoutRequest,
  RegisterUserRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  oktaLogin
} from '../api/requests';
import type ApiClient from '~/common/services/ApiClient';
import type { UserStore } from '../stores/user';
import { FeaturesEnum } from '~/features/types';
import { RolesEnum } from '~/roles/types';
import { UserRegisterRequest } from '~/users/types';
import { PasswordReset } from '../types';

export class AuthenticationService {
  apiClient: ApiClient;
  userStore: UserStore;

  constructor(apiClient: ApiClient, userStore: UserStore) {
    this.apiClient = apiClient;
    this.userStore = userStore;
  }

  async authenticate(username: string, password: string): Promise<boolean> {
    return this.apiClient
      .request(new LoginRequest(username, password))
      .then((response) => {
        this.userStore.isAuthenticated = true;
        this.userStore.accessToken = response.token;
        this.userStore.id = response.data.userID;
        this.syncUserData(response.data);

        return Promise.resolve(true);
      });
  }

  async autoLogin(token: string): Promise<boolean> {
    try {
      const response = await this.apiClient.autoLogin(token);
      this.userStore.isAuthenticated = true;
      this.userStore.accessToken = response.token;
      this.userStore.id = response.data.userID;
      this.syncUserData(response.data);
      return true;
    } catch (error) {
      console.error('Auto-login failed:', error);
      return false;
    }
  }

  async unauthenticate() {
    const accessToken = this.userStore.accessToken;
  
    const response = await this.apiClient.request(
      new LogoutRequest(accessToken)
    );
  
    this.userStore.isAuthenticated = false;
    this.userStore.accessToken = '';
    this.userStore.id = '';
    this.userStore.setRoles([]);
    this.userStore.setPermissions([]);
    this.userStore.setCompany(null);
    this.userStore.setFeatures([]);
  
    return response;
  }

  async register(user: UserRegisterRequest) {
    const request = new RegisterUserRequest(user);

    return this.apiClient.request(request);
  }

  private getUserFeatures(
    companyFeatures: any[],
    groups: any[],
    roles: any[],
    importantFeatures: string[],
  ): any[] {
    // Check if the user has no roles
    if (roles.length === 0) {
      return []; // No roles means no features
    }

    // Updated isAdmin check to include 'SuperAdmin'
    const isAdmin = roles.some((role) =>
      [RolesEnum.ADMIN, RolesEnum.SUPER_ADMIN].includes(role.name),
    );

    const groupFeatures = groups.flatMap((group) => group.features.data);

    const importantCompanyFeatures = companyFeatures.filter((feature) =>
      importantFeatures.includes(feature.name),
    );

    if (isAdmin) {
      // Admin or SuperAdmin users get all company features
      return [...companyFeatures];
    }

    if (groupFeatures.length > 0) {
      // Non-admin users with group-specific features
      return [
        ...groupFeatures,
        ...importantCompanyFeatures.filter(
          (importantFeature) =>
            !groupFeatures.some(
              (feature) => feature.name === importantFeature.name,
            ),
        ),
      ];
    }

    // Non-admin users with no group-specific features
    return [
      ...companyFeatures,
      ...importantCompanyFeatures.filter(
        (importantFeature) =>
          !companyFeatures.some(
            (feature) => feature.name === importantFeature.name,
          ),
      ),
    ];
  }

  async syncUserData(response: any) {
    const importantFeatures = [FeaturesEnum.STARS]; // Define important to keep
    this.userStore.setRoles(response.roles.data);
    this.userStore.setPermissions(response.permissions.data);
    this.userStore.setCompany(response.company.data);

    const combinedFeatures = this.getUserFeatures(
      response.company?.data?.features?.data ?? [],
      response.groups?.data ?? [],
      response.roles.data,
      importantFeatures,
    );

    this.userStore.setFeatures(combinedFeatures);
  }

  async forgotPassword(username: string): Promise<any> {
    return this.apiClient.request(new ForgotPasswordRequest(username));
  }

  async resetPassword(passwordReset: PasswordReset): Promise<any> {
    return this.apiClient.request(new ResetPasswordRequest(passwordReset));
  }
  async oktaLogin(email: string): Promise<any> {
    return this.apiClient.request(new oktaLogin(email));
  }
}
